import { supabase, DEFAULT_ORG_ID, applyOrgFilter, getActiveOrgOverride, signImagesDeep } from '../supabaseClient'
import { getCurrentUser, getCurrentStaff } from './auth'
import { isValidCurp, normalizeCurp } from '../curp'
import { normalizeName } from '../csvPlayers'
import { canCreate } from './billing'
import { gateConsentimientoActivo } from './consent'
import { temporadaDestino, evaluarCandado, candadoAplica } from '../candadoGrupo'
import { EMBED_RESPONSABLE, conResponsable } from '../familias'

// Fecha (sin hora) para el rastro de membresia de grupo. Postgres la acepta tanto
// en columnas date como timestamptz, y sin hora no depende de la zona horaria del
// navegador: para "desde cuando estuvo en el equipo" el dia es la precision util.
const hoy = () => new Date().toISOString().slice(0, 10)

// =====================================================
// CRUD de jugadores
// =====================================================

export const createPlayer = async (playerData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    if (!(await canCreate(orgId, 'players'))) {
      return { data: null, error: { code: 'PLAN_LIMIT', resource: 'players', message: 'Alcanzaste el límite de jugadores de tu plan. Mejora tu plan para agregar más.' } }
    }

    // CURP gobernada por activación: para nacer ACTIVO se exige CURP válida.
    const willBeActive = (playerData.estatus || 'Activo') === 'Activo'
    if (willBeActive && !isValidCurp(playerData.curp)) {
      return { data: null, error: new Error('Captura una CURP válida para activar al jugador.') }
    }
    // Gate de consentimiento. REGISTRAR siempre se puede; ACTIVAR es lo que exige
    // el consentimiento. Por eso aqui no se rechaza: el jugador nace Pendiente y
    // se activa cuando el tutor firme. Rechazar seria un callejon sin salida --
    // el consentimiento se pide SOBRE un jugador, asi que sin poder crearlo no
    // habria forma de conseguirlo, y un club nuevo no podria registrar ni al
    // primero.
    let estatusEfectivo = playerData.estatus || 'Activo'
    let pendientePorConsentimiento = false
    if (willBeActive) {
      const gate = await gateConsentimientoActivo({ orgId })
      if (!gate.ok) {
        estatusEfectivo = 'Pendiente'
        pendientePorConsentimiento = true
      }
    }

    // nombres/apellidos van explicitos: si solo se mandara nombre_completo, el
    // trigger _jugador_sync_nombre() lo volveria a partir con partir_nombre() y
    // tiraria lo que la persona escribio a mano. El formulario ya captura las dos
    // partes por separado justo para dejar de adivinar donde termina el nombre.
    const record = {
      nombre_completo: playerData.nombre_completo,
      nombres: playerData.nombres || null,
      apellidos: playerData.apellidos || null,
      nickname: playerData.nickname || null,
      numero: playerData.numero || null,
      genero: playerData.genero || null,
      fecha_nacimiento: playerData.fecha_nacimiento || null,
      curp: playerData.curp || null,
      telefono_jugador: playerData.telefono_jugador || null,
      contacto: playerData.tutor_nombre || playerData.contacto || null,
      telefono_contacto: playerData.tutor_telefono || playerData.telefono_contacto || null,
      email_tutor: playerData.email_tutor || null,
      estatus: estatusEfectivo,
      activo: true,
      deleted: false,
      org_id: orgId,
    }

    const { data, error } = await supabase
      .from('jugadores')
      .insert(record)
      .select()
      .single()

    if (error) throw error

    // Crear membresia automatica con status mapeado
    const statusMap = { Activo: 'active', Pendiente: 'pending', Suspendido: 'suspended', Inactivo: 'inactive', Baja: 'retired' }
    await supabase
      .from('player_club_memberships')
      .insert({
        jugador_id: data.id,
        org_id: orgId,
        status: statusMap[estatusEfectivo] || 'active',
        es_principal: true,
        joined_at: new Date().toISOString().split('T')[0],
        dorsal_principal: playerData.numero ? parseInt(playerData.numero) : null,
      })

    // El aviso viaja aparte del error: la pantalla debe poder decir POR QUE quedo
    // pendiente sin que el alta parezca haber fallado.
    return { data, error: null, aviso: pendientePorConsentimiento
      ? 'El jugador quedó Pendiente: este club exige el consentimiento del tutor para activar.'
      : null }
  } catch (error) {
    console.error('[createPlayer]', error)
    return { data: null, error }
  }
}

export const updatePlayer = async (playerId, playerData) => {
  try {
    const updates = {}
    const fields = [
      'nombre_completo', 'nombres', 'apellidos', 'nickname', 'numero', 'fecha_nacimiento',
      'curp', 'telefono_jugador', 'email_tutor', 'estatus', 'activo'
    ]

    fields.forEach((f) => {
      if (playerData[f] !== undefined) updates[f] = playerData[f]
    })

    // Mapear campos con alias
    if (playerData.genero !== undefined) {
      updates.genero = playerData.genero
    }
    if (playerData.tutor_nombre !== undefined) {
      updates.contacto = playerData.tutor_nombre
    }
    if (playerData.tutor_telefono !== undefined) {
      updates.telefono_contacto = playerData.tutor_telefono
    }

    updates.updated_at = new Date().toISOString()

    // Gate de consentimiento al PASAR a Activo (solo si el club lo exige).
    //
    // Se compara contra el estatus que el jugador tiene HOY, no contra el que trae el
    // payload: el formulario manda siempre el estatus actual, asi que mirar solo el
    // payload bloqueaba TODA edicion de un jugador ya activo sin consentimiento --
    // su telefono, su CURP, y sobre todo su RESPONSABLE, que es justo a quien hay que
    // pedirle la firma. La puerta bloqueaba el arreglo de su propia condicion, y el
    // admin quedaba sin mas salida que descartar los cambios.
    //
    // Bloquear a quien ya esta activo tampoco lo desactiva: solo impide corregirlo.
    if (updates.estatus === 'Activo') {
      const { data: actual } = await supabase
        .from('jugadores')
        .select('estatus')
        .eq('id', playerId)
        .single()

      if (actual?.estatus !== 'Activo') {
        const gate = await gateConsentimientoActivo({ jugadorId: playerId })
        if (!gate.ok) return { data: null, error: gate.error }
      }
    }

    const { data, error } = await supabase
      .from('jugadores')
      .update(updates)
      .eq('id', playerId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePlayer]', error)
    return { data: null, error }
  }
}

export const deletePlayer = async (playerId) => {
  try {
    const { data, error } = await supabase
      .from('jugadores')
      .update({
        deleted: true,
        deleted_at: new Date().toISOString(),
        activo: false,
        estatus: 'Baja',
      })
      .eq('id', playerId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[deletePlayer]', error)
    return { data: null, error }
  }
}

// Llaves de dedup de los jugadores de la org activa: CURPs y nombre+fecha.
export const getExistingPlayerKeys = async () => {
  try {
    let query = supabase
      .from('jugadores')
      .select('curp, nombre_completo, fecha_nacimiento')
      .eq('deleted', false)
    query = applyOrgFilter(query)
    const { data, error } = await query
    if (error) throw error

    const curps = new Set()
    const nameDob = new Set()
    ;(data || []).forEach((j) => {
      const c = normalizeCurp(j.curp)
      if (c) curps.add(c)
      if (j.nombre_completo && j.fecha_nacimiento) {
        nameDob.add(`${normalizeName(j.nombre_completo)}|${j.fecha_nacimiento}`)
      }
    })
    return { data: { curps, nameDob }, error: null }
  } catch (error) {
    console.error('[getExistingPlayerKeys]', error)
    return { data: { curps: new Set(), nameDob: new Set() }, error }
  }
}

// Padron minimo de la org para avisar de altas repetidas mientras se captura.
// Se trae completo una vez al abrir el formulario: son cientos de filas de tres
// campos, y comparar en memoria evita una consulta por cada tecla.
export const getPadronParaDuplicados = async () => {
  try {
    let query = supabase
      .from('jugadores')
      .select('id, nombre_completo, curp, fecha_nacimiento, estatus')
      .eq('deleted', false)
    query = applyOrgFilter(query)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPadronParaDuplicados]', error)
    return { data: [], error }
  }
}

export const getPlayerById = async (playerId) => {
  try {
    const { data, error } = await supabase
      .from('jugadores')
      .select(`
        *,
        familias!left(id, nombre, ${EMBED_RESPONSABLE}),
        jugador_personas!left(parentesco, personas!left(id, nombre_completo, telefono, email)),
        jugadores_meta(meta_key, meta_value),
        jugadores_grupos!left(
          id, grupo_id, estatus, activo, deleted,
          numero_jersey, nickname, posicion, notas,
          fecha_alta, fecha_baja,
          grupos(id, nombre_del_grupo, deporte)
        )
      `)
      .eq('id', playerId)
      .single()

    if (error) throw error
    await signImagesDeep(data)
    if (data?.familias) data.familias = conResponsable(data.familias)
    return { data, error: null }
  } catch (error) {
    console.error('[getPlayerById]', error)
    return { data: null, error }
  }
}

export const updatePlayerGroupOverrides = async (jugadorGrupoId, overrides) => {
  try {
    const updates = {}
    const fields = ['numero_jersey', 'nickname', 'posicion', 'notas']
    fields.forEach((f) => {
      if (overrides[f] !== undefined) updates[f] = overrides[f]
    })

    const { data, error } = await supabase
      .from('jugadores_grupos')
      .update(updates)
      .eq('id', jugadorGrupoId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePlayerGroupOverrides]', error)
    return { data: null, error }
  }
}

// Guarda en bloque el numero de camiseta por grupo (numero_jersey) de varias membresias.
// entries = [{ jugadorGrupoId, numero }]; numero vacio/no numerico => null.
export const setNumerosGrupo = async (grupoId, entries = []) => {
  try {
    for (const e of entries) {
      const num = (e.numero === '' || e.numero == null) ? null : parseInt(e.numero, 10)
      const { error } = await supabase
        .from('jugadores_grupos')
        .update({ numero_jersey: Number.isNaN(num) ? null : num })
        .eq('id', e.jugadorGrupoId)
        .eq('grupo_id', grupoId)
      if (error) throw error
    }
    return { error: null }
  } catch (error) {
    console.error('[setNumerosGrupo]', error)
    return { error }
  }
}

export const savePlayerMeta = async (jugadorId, metaKey, metaValue) => {
  try {
    const { data, error } = await supabase
      .from('jugadores_meta')
      .upsert(
        {
          jugador_id: jugadorId,
          meta_key: metaKey,
          meta_value: metaValue,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'jugador_id,meta_key' }
      )
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[savePlayerMeta]', error)
    return { data: null, error }
  }
}

export const getAllPlayersForAssignment = async ({ groupId = null, orgId = null, clubId = null } = {}) => {
  try {
    const effectiveOrgId = orgId || clubId || getActiveOrgOverride()  // fallback to org override
    const currentUser = await getCurrentUser()
    const isAdmin = currentUser?.user_metadata?.rol === 'admin'

    if (!isAdmin) {
      const { data, error } = await supabase.rpc('get_players_for_assignment', {
        p_group_id: groupId,
        p_org_id: effectiveOrgId
      })

      if (!error) {
        await signImagesDeep(data)
        return { data, error: null }
      }

      // If the RPC is not yet deployed or fails with a known missing-function error, fall back to the legacy query
      const missingFunctionCodes = ['42883', 'PGRST204', 'PGRST200']
      if (!missingFunctionCodes.includes(error?.code)) {
        throw error
      }
    }

    let query = supabase
      .from('jugadores')
      .select('*')
      .or('deleted.is.null,deleted.eq.false')
      .eq('activo', true)
      .neq('estatus', 'Baja')
      .order('nombre_completo')

    query = applyOrgFilter(query, effectiveOrgId)

    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    return { data, error: null }
  } catch (error) {
    return { data: null, error }
  }
};

export const getAllPlayersWithGroups = async () => {
  try {
    const withDeletedFilter = () => {
      let q = supabase
        .from('jugadores')
        .select(`
          *,
          familias!left(id, nombre, ${EMBED_RESPONSABLE}),
          jugadores_meta(
            meta_key,
            meta_value
          ),
          jugadores_grupos!left (
            id,
            activo,
            estatus,
            deleted,
            grupo_id,
            grupos (
              id,
              nombre_del_grupo,
              deporte
            )
          ),
          temporada_inscripciones!left (
            deporte_id,
            estatus,
            temporadas ( estado )
          )
        `)
        .or('deleted.is.null,deleted.eq.false')
        .order('nombre_completo');
      return applyOrgFilter(q);
    };

    const withoutDeletedFilter = () => {
      let q = supabase
        .from('jugadores')
        .select(`
          *,
          jugadores_meta(
            meta_key,
            meta_value
          ),
          jugadores_grupos!left (
            id,
            estatus,
            grupo_id,
            grupos (
              id,
              nombre_del_grupo,
              deporte
            )
          )
        `)
        .order('nombre_completo');
      return applyOrgFilter(q);
    };

    const conFamilia = (lista) => (lista || []).map((p) => ({ ...p, familias: conResponsable(p.familias) }));
    const primary = await withDeletedFilter();
    if (!primary.error) {
      await signImagesDeep(primary.data);
      return { data: conFamilia(primary.data), error: null };
    }

    const fallback = await withoutDeletedFilter();
    if (fallback.error) throw fallback.error;
    await signImagesDeep(fallback.data);
    return { data: conFamilia(fallback.data), error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Candado suave (F2): para entrar a un grupo hay que estar inscrito en la
// temporada abierta de su deporte ('pendiente' basta). Devuelve la temporada
// destino y quien no pasa, SIN escribir nada. La UI lo usa para ofrecer
// "inscribir y asignar" en un clic; assignPlayersToGroup lo aplica por su cuenta
// para que ningun otro llamador se lo salte.
export const revisarCandadoGrupo = async (groupId, playerIds) => {
  const uniqueIds = Array.from(new Set(playerIds || []));
  const { data: grupo, error: gErr } = await supabase
    .from('grupos').select('deporte, org_id').eq('id', groupId).single();
  if (gErr) return { data: null, error: gErr };

  const { data: abiertas, error: tErr } = await supabase
    .from('temporadas')
    .select('id, org_id, deporte_id, nombre, estado')
    .eq('org_id', grupo.org_id)
    .eq('deporte_id', grupo.deporte)
    .eq('estado', 'abierta')
    .is('deleted_at', null);
  if (tErr) return { data: null, error: tErr };

  const temporada = temporadaDestino(abiertas || []);
  // Sin temporada abierta (o con dos, ambiguo) el candado no aplica: no hay
  // donde inscribir, y bloquear dejaria al club sin poder armar equipos.
  if (!candadoAplica(temporada)) {
    return { data: { temporada: null, pasan: uniqueIds, faltanInscripcion: [] }, error: null };
  }

  const { data: inscs, error: iErr } = await supabase
    .from('temporada_inscripciones')
    .select('jugador_id, estatus')
    .eq('temporada_id', temporada.id)
    .in('jugador_id', uniqueIds);
  if (iErr) return { data: null, error: iErr };

  const inscripcionesByJugador = {};
  (inscs || []).forEach((i) => { inscripcionesByJugador[i.jugador_id] = { estatus: i.estatus }; });
  const { pasan, faltanInscripcion } = evaluarCandado({ jugadorIds: uniqueIds, inscripcionesByJugador });
  return { data: { temporada, pasan, faltanInscripcion }, error: null };
};

export const assignPlayersToGroup = async (groupId, playerIds) => {
  try {
    const { data: groupData, error: groupError } = await supabase
      .from('grupos')
      .select('deporte')
      .eq('id', groupId)
      .single();

    const groupSport = !groupError ? groupData?.deporte || null : null;

    const uniqueIds = Array.from(new Set(playerIds));

    // Candado suave: no se asigna a NADIE si alguno no esta inscrito. Parcial
    // seria peor (el admin creeria que quedaron los 20 y quedaron 17). La UI
    // atrapa este error y ofrece inscribirlos en un clic.
    const { data: candado, error: candadoError } = await revisarCandadoGrupo(groupId, uniqueIds);
    if (candadoError) throw candadoError;
    if (candado.faltanInscripcion.length) {
      return {
        data: null,
        error: {
          code: 'SIN_INSCRIPCION',
          jugadorIds: candado.faltanInscripcion,
          temporada: candado.temporada,
          message: `${candado.faltanInscripcion.length} jugador(es) no están inscritos en "${candado.temporada?.nombre}".`,
        },
      };
    }

    const { data: existingRows, error: existingError } = await supabase
      .from('jugadores_grupos')
      .select('id, jugador_id, activo')
      .eq('grupo_id', groupId)
      .in('jugador_id', uniqueIds);

    if (existingError) throw existingError;

    const existingIds = new Set((existingRows || []).map((row) => row.jugador_id));
    const toInsert = uniqueIds.filter((playerId) => !existingIds.has(playerId));
    // Reingreso vs ya activo: al que vuelve se le abre fecha_alta nueva; al que ya
    // estaba NO se le toca, o se le borraria la fecha en que realmente entro.
    const reingresan = (existingRows || [])
      .filter((row) => !row.activo && uniqueIds.includes(row.jugador_id))
      .map((row) => row.jugador_id);
    const yaActivos = uniqueIds.filter(
      (id) => existingIds.has(id) && !reingresan.includes(id),
    );

    let insertData = [];
    if (toInsert.length > 0) {
      const assignments = toInsert.map((playerId) => ({
        id: crypto.randomUUID(),
        jugador_id: playerId,
        grupo_id: groupId,
        activo: true,
        estatus: 'Activo',
        deleted: false,
        fecha_alta: hoy()
      }));

      const { data, error } = await supabase
        .from('jugadores_grupos')
        .insert(assignments)
        .select();

      if (error) throw error;
      insertData = data || [];
    }

    if (reingresan.length > 0) {
      const { error: updateError } = await supabase
        .from('jugadores_grupos')
        .update({ activo: true, deleted: false, estatus: 'Activo', fecha_alta: hoy(), fecha_baja: null })
        .eq('grupo_id', groupId)
        .in('jugador_id', reingresan);

      if (updateError) throw updateError;
    }

    if (yaActivos.length > 0) {
      const { error: updateError } = await supabase
        .from('jugadores_grupos')
        .update({ activo: true, deleted: false, estatus: 'Activo' })
        .eq('grupo_id', groupId)
        .in('jugador_id', yaActivos);

      if (updateError) throw updateError;
    }

    if (groupSport) {
      await supabase.functions.invoke('recalculate-player-metadata', {
        body: { player_ids: uniqueIds }
      });
    }

    return { data: insertData, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const removePlayersFromGroup = async (groupId, playerIds) => {
  try {
    const { data, error } = await supabase
      .from('jugadores_grupos')
      .update({ activo: false, deleted: false, estatus: 'Inactivo', fecha_baja: hoy() })
      .eq('grupo_id', groupId)
      .in('jugador_id', playerIds);

    if (error) throw error;
    await supabase.functions.invoke('recalculate-player-metadata', {
      body: { player_ids: Array.from(new Set(playerIds)) }
    });
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const recalculatePlayerMetadata = async (payload = {}) => {
  try {
    const { data, error } = await supabase.functions.invoke('recalculate-player-metadata', {
      body: payload
    });
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const uploadPlayerImage = async (playerId, { image_base64, filename }) => {
  try {
    if (!playerId || !image_base64) {
      throw new Error('Player ID and image data are required');
    }

    // Edge function (envía el JWT del usuario para auth/validación de org)
    const { data: result, error } = await supabase.functions.invoke('upload-player-image', {
      body: {
        player_id: playerId,
        image_base64: image_base64,
        filename: filename || `${playerId}_${Date.now()}.jpg`,
        content_type: 'image/jpeg'
      },
    });

    if (error) throw error;
    if (result?.error) {
      return { data: null, error: result.error };
    }
    return { data: result, error: null };
  } catch (error) {
    console.error('Upload player image error:', error);
    return { data: null, error: error.message };
  }
};

export const removePlayerImage = async (playerId) => {
  try {
    if (!playerId) {
      throw new Error('Player ID is required');
    }

    const { data: result, error } = await supabase.functions.invoke('upload-player-image', {
      body: { player_id: playerId, remove: true },
    });

    if (error) throw error;
    if (result?.error) {
      return { data: null, error: result.error };
    }
    return { data: result, error: null };
  } catch (error) {
    console.error('Remove player image error:', error);
    return { data: null, error: error.message };
  }
};

